// Phase 25 iter-6 — Card para configurar metadata do rol TRANSPORTISTA.
//
// Recolle os datos máis críticos do MR504A (autorización de transporte
// de animais vivos):
//   • Matrícula do vehículo
//   • Número de autorización de transportista (transporter_auth_no)
//   • Tipo de autorización (Tipo I / II)
//   • Data de expiración
//   • Certificado de competencia (competence_cert_no)
//
// O backend persiste como UserRoleAssignment.transporter_* e expón o
// estado de validación en transporter_auth_status / vehicle_auth_status.

import { CheckCircle2, Truck } from 'lucide-react';
import { useEffect, useState } from 'react';

import { listMyRoles } from '../../api/roles';
import { updateRoleMetadata } from '../../api/roleMetadata';
import { describeAxiosError } from '../../api/errors';
import Spinner from '../ui/Spinner';
import { useToast } from '../../context/ToastContext';

interface FormState {
  vehicle_plate: string;
  transporter_auth_no: string;
  transporter_auth_type: string;
  transporter_auth_expiry: string;
  competence_cert_no: string;
}

const EMPTY: FormState = {
  vehicle_plate: '',
  transporter_auth_no: '',
  transporter_auth_type: 'TIPO_I',
  transporter_auth_expiry: '',
  competence_cert_no: '',
};

export function TransporterSetupCard(): JSX.Element {
  const { addToast } = useToast();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listMyRoles()
      .then((r) => {
        const a = r.roles.find((x) => x.role === 'TRANSPORTISTA');
        if (!a) return;
        setForm({
          vehicle_plate: a.vehicle_plate ?? '',
          transporter_auth_no: a.transporter_auth_no ?? '',
          transporter_auth_type: a.transporter_auth_type ?? 'TIPO_I',
          transporter_auth_expiry: a.transporter_auth_expiry
            ? a.transporter_auth_expiry.slice(0, 10)
            : '',
          competence_cert_no: a.competence_cert_no ?? '',
        });
        setSaved(Boolean(a.vehicle_plate && a.transporter_auth_no && a.transporter_auth_expiry));
      })
      .catch((e) => setError(describeAxiosError(e, 'Non se puido cargar a configuración.')))
      .finally(() => setLoading(false));
  }, []);

  function setField<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!form.vehicle_plate.trim() || !form.transporter_auth_no.trim()) {
      setError('Matrícula e número de autorización son obrigatorios.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const payload = {
        vehicle_plate: form.vehicle_plate.trim().toUpperCase(),
        transporter_auth_no: form.transporter_auth_no.trim(),
        transporter_auth_type: form.transporter_auth_type,
        ...(form.transporter_auth_expiry
          ? { transporter_auth_expiry: new Date(form.transporter_auth_expiry).toISOString() }
          : {}),
        ...(form.competence_cert_no.trim()
          ? { competence_cert_no: form.competence_cert_no.trim() }
          : {}),
      };
      await updateRoleMetadata('TRANSPORTISTA', payload);
      setSaved(true);
      addToast('Configuración de transporte gardada.', 'success');
    } catch (err) {
      setError(describeAxiosError(err, 'Non se puido gardar a configuración.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="border border-border rounded-2xl p-6 bg-white">
      <div className="flex items-center gap-2 mb-4">
        <Truck size={18} className="text-muted" />
        <h3 className="font-semibold text-dark text-base">Setup como transportista</h3>
      </div>

      <p className="text-sm text-muted mb-4">
        Para documentar movements (recollidas e entregas) precisamos os datos do MR504A. O equipo
        valida o número de autorización en 24-48 h.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted">
          <Spinner size={16} /> Cargando…
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field
              label="Matrícula"
              value={form.vehicle_plate}
              onChange={(v) => setField('vehicle_plate', v)}
              placeholder="1234ABC"
            />
            <Field
              label="Nº autorización (MR504A)"
              value={form.transporter_auth_no}
              onChange={(v) => setField('transporter_auth_no', v)}
              placeholder="ES-MR504A-..."
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-muted font-medium block mb-1">
                Tipo
              </span>
              <select
                value={form.transporter_auth_type}
                onChange={(e) => setField('transporter_auth_type', e.target.value)}
                className="airbnb-input"
              >
                <option value="TIPO_I">Tipo I (curta distancia)</option>
                <option value="TIPO_II">Tipo II (longa distancia)</option>
              </select>
            </label>
            <Field
              label="Caduca"
              type="date"
              value={form.transporter_auth_expiry}
              onChange={(v) => setField('transporter_auth_expiry', v)}
            />
          </div>
          <Field
            label="Cert. competencia (opcional)"
            value={form.competence_cert_no}
            onChange={(v) => setField('competence_cert_no', v)}
            placeholder="CCT-..."
          />
          {error && <p className="text-xs text-rauxa">{error}</p>}
          <button type="submit" disabled={saving} className="btn-primary !py-2 !px-4 text-sm">
            {saving ? <Spinner size={14} /> : 'Gardar'}
          </button>
        </form>
      )}

      {saved && !error && (
        <div className="mt-4 p-3 rounded-xl flex items-center gap-2 bg-rauxa/10 text-rauxa text-sm">
          <CheckCircle2 size={16} /> Configuración rexistrada. En validación.
        </div>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-muted font-medium block mb-1">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="airbnb-input"
      />
    </label>
  );
}
